const operationsDefaults = require('./defaults/operations');
const constants = require('../constants');

class ToolsOperationsFactory {
  /**
   * @param {DiContainer} injector
   *
  */
  constructor() {
    this.defaults = {
      operations: operationsDefaults,
    };
  }

  create(group, type, subType, data = {}) {
    const result = {
      actionTime: Date.now(),
      properties: {
        ...data,
        group,
        type,
        ...(typeof subType === 'undefined' || subType === false
          ? {}
          : { subType: !subType ? null : subType }
        ),
      },
    };

    return result;
  }

  // Docs
  // https://pdffiller.atlassian.net/wiki/display/PD/Rules+for+fillable+Date
  template({
    type = 'text', subType = 'none', template = {}, pageId = 0,
  }) {
    template = {
      ...this.fillTemplateWithDefaults({
        type,
        subType,
        ...template,
      }),
      ...template,
    };

    if (template.pageId) delete template.pageId;

    return this.create(
      constants.GROUP.TOOLS,
      type,
      template.subType || 'none',
      {
        pageId,
        template,
      }
    );
  }

  content({
    type = 'text', subType = constants.SUB_TYPE.NONE,
    content, pageId = 0,
  }) {
    if (!type) type = content.type;

    if (!subType && type) {
      subType = this._getContentOpSubType(content);
    }

    if (!content.type) content.type = type; else type = content.type;
    if (!content.subType) content.subType = subType; else subType = content.subType;

    const data = { content, pageId };

    return this.create(constants.GROUP.TOOLS, type, subType, data);
  }

  pages(pages, additionalProperties = {}) {
    return this.create(
      constants.GROUP.TOOLS,
      constants.TYPE.PAGES,
      false,
      { pages, ...additionalProperties }
    );
  }

  comment(commentData, pageId) {
    const { general = {}, ...rest } = commentData;
    const content = { ...general, ...rest };

    return {
      actionTime: Date.now(),
      properties: {
        content,
        group: constants.GROUP.TOOLS,
        pageId,
        subType: constants.SUB_TYPE.NONE,
        type: constants.TYPE.COMMENT,
      },
    };
  }

  /**
   *
   * data.access
   *  - subType: string
   * config.Busy
   *  - timeout
   *  - location
   *  - message
   * editorDefaults
   * // pdfObject
   *
   */
  initOperations(data = {}) {
    const { accessOperation, accessLevel } = this.accessOperation(data.access, data.config);
    const defaultsOperation = this.defaultsOperation(data.editorDefaults);
    const featuresOperation = this.featuresOperation(data.editorFeatures);
    const scenariosOperation = this.scenariosOperation(data.editorScenarios);
    const sourceOperation = this.sourceOperation(data.pdfObject);

    return {
      operations: {
        ...(accessOperation ? { accessOperation } : {}),
        ...(defaultsOperation ? { defaultsOperation } : {}),
        ...(featuresOperation ? { featuresOperation } : {}),
        ...(scenariosOperation ? { scenariosOperation } : {}),
        ...(sourceOperation ? { sourceOperation } : {}),
      },
      ...(accessLevel ? { accessLevel } : {}),
    };
  }

  accessOperation(accessData, config = {}) {
    if (!accessData) return {};

    const accessLevel = accessData.subType;

    if (accessLevel === constants.ACCESS.DENIED) {
      return {
        accessOperation: this.accessDenied(accessData),
        accessLevel,
      };
    }

    if (this._getAuthAccessIsBusy(accessLevel)) {
      return {
        accessOperation: this.accessBusy(accessData, accessLevel, config.Busy),
        accessLevel,
      };
    }

    return {
      accessOperation: this.create(
        constants.GROUP.DOCUMENT,
        constants.TYPE.ACCESS,
        accessLevel,
        accessData, // editorAuthData.document.access
      ),
      accessLevel,
    };
  }

  accessBusy(accessData, accessLevel, config = {}) {
    const { GROUP, TYPE, ACCESS } = constants;
    const {
      timeout, location, message, busyUser,
    } = {
      timeout: config.timeout,
      location: config.location,
      message: config.message,
      ...accessData,
    };

    const structure = this.create(
      GROUP.DOCUMENT,
      TYPE.ACCESS,
      accessLevel || ACCESS.BUSY,
      {
        timeout, location, message, busyUser,
      }
    );

    structure.id = { clientId: 1, localId: 1 };

    return structure;
  }

  accessDenied(data = {}) {
    const structure = this.create(
      constants.GROUP.DOCUMENT,
      constants.TYPE.ACCESS,
      constants.ACCESS.DENIED,
      { location: data.location }
    );

    structure.id = { clientId: 1, localId: 1 };

    return structure;
  }

  /**
   * editorDefaults - structure like
    {
      "content": [
        {
          "id": "tools.text.*",
          ...
        },
        ...
      ],
      "template": [
        {
          "id": "tools.text.*",
          ...
        },
        ...
      ],
      "customDictionary": [],
      "useSpellCheck": false,
      "useEmbeddedFonts": true,
      "useSigDateStamp": true,
      "todoListState": "minimized",
      "textFieldViewMode": "stretch",
      "fillableWizardState": "lite"
    }
   */
  defaultsOperation(editorDefaults) {
    if (!editorDefaults) return false;

    return this.create(
      constants.GROUP.EDITOR,
      constants.TYPE.DEFAULTS,
      false,
      editorDefaults
    );
  }

  featuresOperation(featuresData) {
    if (!featuresData) return false;

    return this.create(
      constants.GROUP.EDITOR,
      constants.TYPE.FEATURES,
      false,
      featuresData
    );
  }

  scenariosOperation(scenariosData) {
    if (!scenariosData) return false;

    return this.create(
      constants.GROUP.EDITOR,
      constants.TYPE.SCENARIOS,
      false,
      scenariosData
    );
  }

  editorMode(mode, { allowed = true, error, elapsedTime }) {
    const modesList = Object.values(constants.EDITOR_MODE);

    if (!modesList.includes(mode)) {
      throw new Error(`Wrong mode. Expected one of ${modesList.join()}`);
    }

    this.create(
      constants.GROUP.EDITOR,
      constants.TYPE.MODE,
      mode,
      { allowed, error, elapsedTime }
    );
  }

  /**
    pdfObject: {
      status: PdfStatus;
      url: string;
      errorLocation: string;
      errorCode?: number;
      version?: string;
      fileId?: string;
    }
   */
  sourceOperation(pdfObject) {
    if (!pdfObject) return false;

    return this.create(
      constants.GROUP.DOCUMENT,
      constants.TYPE.SOURCE,
      false,
      {
        pdf: {
          status: constants.PDF_STATUS.FINISHED,
          ...pdfObject,
        },
      }
    );
  }

  _getAuthAccessIsBusy(accessLevel) {
    return [
      constants.ACCESS.BUSY,
      constants.ACCESS.STRONG_BUSY,
      constants.ACCESS.SWITCH_ENV,
    ].includes(accessLevel);
  }

  attribute(attributeKey, attributeData) {
    return this.create(constants.GROUP.TOOLS, 'attributes', attributeKey, { content: attributeData });
  }

  fillTemplateWithDefaults(template) {
    if (!template) return {};
    const type = template.type || 'text';
    const defaults = this.defaults.operations[type];

    if (!defaults) return template;

    return {
      ...defaults,
      ...template,
    };
  }

  _getContentOpSubType(element) {
    const { type, subType, linkedEraseId } = element;
    const { TYPE, SUB_TYPE } = constants;

    if (type === TYPE.SIGNATURE) return SUB_TYPE.NONE;
    if (linkedEraseId) return SUB_TYPE.OVERLYING;

    if (!subType) return 'none';

    return subType;
  }
}

module.exports = new ToolsOperationsFactory();
